/**
 * QuotaCoinContext
 * Manages the quota coin flip notification state.
 * Shows a single coin at a time — rapid actions replace the current coin.
 */

import type { QuotaCoinData } from '@parlay-picker/shared/quotaCoin';
import { createContext, type ReactNode, useCallback, useContext, useRef, useState } from 'react';
import { useAuth } from './AuthContext';

interface QuotaCoinContextValue {
  coin: QuotaCoinData | null;
  showCoin: (data: QuotaCoinData) => void;
  dismissCoin: () => void;
}

const QuotaCoinContext = createContext<QuotaCoinContextValue | null>(null);

const AUTO_DISMISS_MS = 3000;

export function QuotaCoinProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [coin, setCoin] = useState<QuotaCoinData | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showQuotaNotifications = user?.preferences?.showQuotaNotifications ?? true;

  const dismissCoin = useCallback(() => {
    setCoin(null);
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const showCoin = useCallback(
    (data: QuotaCoinData) => {
      // Check user preference (default: ON)
      if (!showQuotaNotifications) return;

      // Clear any existing timer
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      setCoin(data);

      timerRef.current = setTimeout(() => {
        setCoin(null);
        timerRef.current = null;
      }, AUTO_DISMISS_MS);
    },
    [showQuotaNotifications],
  );

  return (
    <QuotaCoinContext.Provider value={{ coin, showCoin, dismissCoin }}>
      {children}
    </QuotaCoinContext.Provider>
  );
}

export function useQuotaCoin(): QuotaCoinContextValue {
  const context = useContext(QuotaCoinContext);
  if (!context) {
    throw new Error('useQuotaCoin must be used within QuotaCoinProvider');
  }
  return context;
}
